import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import {
  FileValidationParams,
  validateFileHeaders,
} from "../utils/validateFileHeaders";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

// Service Provider Cost Type Enum
export type ServiceProviderCostTypeEnum = "MNP" | "HLR" | "MNP_HLR";

export const SERVICE_PROVIDER_COST_TYPE_OPTIONS: { label: string; value: ServiceProviderCostTypeEnum }[] = [
  { label: "MNP", value: "MNP" },
  { label: "HLR", value: "HLR" },
  { label: "MNP & HLR", value: "MNP_HLR" },
];

export interface IExport {
  id: string;
  usedBy: string;
  serviceProviderId: string;
  createdBy: number;
  createdDate: Date;
  status: string;
  attachmentId?: number;
  fileName?: string;
}
export interface ServiceProviderCostType {
  id: string;
  cost: number;
  effectiveDate: Date;
  comment: string;
  isDeleted?: boolean;
  newCost: number;
  updatedBy?: string;
  operatorId: number;
  serviceProviderId: string;
  type: ServiceProviderCostTypeEnum;
}

export interface ServiceProviderCostHistory {
  id: string;
  snapshot: ServiceProviderCostType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  status: string;
  createdBy: string;
}

export interface ServiceProviderCostHistoryDiff {
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  snapshot: ServiceProviderCostType & { timestamp: string };
  diff?: Record<string, string>;
}

export const exportServiceProviderCost = async (
  params: Params<ServiceProviderCostType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/service-provider-costs/csv", {
      params,
      responseType: "blob",
    });

    // Extract filename from Content-Disposition header
    const disposition = response.headers["content-disposition"];
    let filename = "ServiceProviderCost.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);
  } catch (error: any) {
    console.error(
      "Error fetching ServiceProviderCost :",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchServiceProviderCost(
  params: Params<ServiceProviderCostType>
): Promise<IPageable<ServiceProviderCostType>> {
  try {
    const response = await axiosInstance.get("/service-provider-costs", { params });
    const data = response.data;
    return data;
  } catch (error: any) {
    console.error("Error fetching service provider costs:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching service provider costs."
    );
  }
}

export const addServiceProviderCostApi = async (data: Partial<ServiceProviderCostType>) => {
  try {
    const response = await axiosInstance.post("/service-provider-costs/upsert", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the service provider cost."
    );
  }
};

export const editServiceProviderCostApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/service-provider-costs/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the service provider cost."
    );
  }
};

export const getServiceProviderCostById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/service-provider-costs/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the service provider cost."
    );
  }
};

export const deleteServiceProviderCostApi = async (ids: string[]): Promise<void> => {
  try {
    await axiosInstance.delete(`/service-provider-costs`, { data: ids });
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the service provider cost."
    );
  }
};

export interface ServiceProviderCostUploadParams {
  mccmnc: string;
  mcc: string;
  mnc: string;
  // rateCount: string;
  rate: string;
  effectiveDate: string;
  operatorName: string;
  countryName: string;
}

export const uploadServiceProviderCostsExcel = async (
  serviceProviderId: string,
  file: File,
  override: boolean,
  uploadParams: ServiceProviderCostUploadParams
): Promise<void> => {
  const extension = file.name.split(".").pop()?.toLowerCase() || "xlsx";

  const validationParams: FileValidationParams = {
    alwaysRequired: [uploadParams.rate],
    optionalFields: [uploadParams.mccmnc, uploadParams.mcc, uploadParams.mnc],
  };

  await validateFileHeaders(file, extension, validationParams);

  const formData = new FormData();
  formData.append("file", file);

  const endpoint = extension === "csv" ? "csv" : "excel";
  const params = {
    serviceProviderId,
    override,
    ...(uploadParams.mcc ? { mccHeader: uploadParams.mcc } : {}),
    ...(uploadParams.mnc ? { mncHeader: uploadParams.mnc } : {}),
    ...(uploadParams.mccmnc ? { mccmncHeader: uploadParams.mccmnc } : {}),
    ...(uploadParams.effectiveDate
      ? { effectiveDateHeader: uploadParams.effectiveDate }
      : {}),
    ...(uploadParams.operatorName
      ? { operatorHeader: uploadParams.operatorName }
      : {}),
    ...(uploadParams.countryName
      ? { countryHeader: uploadParams.countryName }
      : {}),
    rateHeader: uploadParams.rate,
    effectiveDateHeader: uploadParams.effectiveDate,
  };

  try {
    const response = await axiosInstance.post(
      `/service-provider-costs/${endpoint}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while uploading the service provider cost file."
    );
  }
};

export const useServiceProviderCostQuery = (
  params: Params<ServiceProviderCostType>
): UseQueryResult<IPageable<ServiceProviderCostType>, Error> => {
    const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["serviceProviderCost", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchServiceProviderCost(effectiveParams),
  });
};

export const ServiceProviderCostBulkApi = async (data: Partial<ServiceProviderCostType>[]) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.put("/service-provider-costs", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Service Provider Cost."
    );
  }
};

export async function fetchServiceProviderImportedFiles(
  params: Params<IExport>
): Promise<IPageable<IExport>> {
  try {
    const finalParams = {
      ...params,
      sort: params.sort || "createdDate,desc",
    };
    console.log("Fetching imported files with params:", finalParams);
    const response = await axiosInstance.get("/imported-files", {
      params: finalParams,
    });
    const data = response.data;
    return data;
  } catch (error: any) {
    console.error("Error fetching service provider costs:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching service provider costs."
    );
  }
}

export const useServiceProviderImportedFilesQuery = (
  params: Params<IExport>
): UseQueryResult<IPageable<IExport>, Error> => {
  const queryKey = ["serviceProviderImportedFiles", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchServiceProviderImportedFiles(params),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 1000,
  });
};

export async function fetchServiceProviderCostHistory(
  id: ServiceProviderCostType["id"]
): Promise<IPageable<ServiceProviderCostType & ServiceProviderCostHistory>> {
  try {
    const response = await axiosInstance.get(`/service-provider-costs/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc" },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the service-provider-cost history for ID ${id}.`
    );
  }
}

export const useServiceProviderNoDataQuery = (
  params: Params<ServiceProviderCostType> = {}
): UseQueryResult<IPageable<ServiceProviderCostType>, Error> => {
  const { comment = "random text to fetch no result", ...rest } = params;
  const fullParams = { comment, ...rest };
  const queryKey = ["serviceProviderCostDetails", JSON.stringify(fullParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchServiceProviderCost(fullParams),
  });
};
